<template>
    <div
        class="drive-home"
        @dragenter.prevent="handleDragEnter"
        @dragover.prevent
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop"
        @paste.prevent="handlePaste"
        @contextmenu.prevent="handleContextMenu"
    >
        <div class="drive-header">
            <a-breadcrumb>
                <a-breadcrumb-item>
                    <a @click="handleHomeClick">
                        <a-icon type="home" style="font-size: 1.5em; cursor: pointer" />
                    </a>
                </a-breadcrumb-item>
                <template v-if="breadcrumbFolders.length > 1">
                    <a-breadcrumb-item
                        v-for="(folder, index) in $_.takeRight(breadcrumbFolders, breadcrumbFolders.length - 1)"
                        :key="folder._id"
                    >
                        <a
                            @click.prevent="handleFolderClick(folder._id, index)"
                            v-if="index < breadcrumbFolders.length - 1"
                        >
                            {{ folder.name }}
                        </a>
                        <span v-else>{{ folder.name }}</span>
                    </a-breadcrumb-item>
                </template>
            </a-breadcrumb>
        </div>

        <div class="drive-toolbar">
            <div class="left-actions">
                <PermissionController :permissions="['drive.create']">
                    <SupsisButton icon="add" primary @click="handleNewFolder"
                        >{{ $t("message.newFolder") }}
                    </SupsisButton>
                </PermissionController>
                <PermissionController :permissions="['drive.create']">
                    <SupsisButton icon="upload_file" primary @click="handleNewFile"
                        >{{ $t("message.newFile") }}
                    </SupsisButton>
                </PermissionController>
            </div>
            <div class="right-actions">
                <div class="view-toggle">
                    <PermissionController :permissions="['drive.delete']">
                        <SupsisButton v-if="selectedFiles.length > 0" icon="select_all" primary @click="selectAllFiles">
                            {{ $t("chatbotv2.selectAll") }}
                        </SupsisButton>
                    </PermissionController>
                    <PermissionController :permissions="['drive.delete']">
                        <SupsisButton v-if="selectedFiles.length > 0" icon="delete" danger @click="handleBulkDelete">
                            {{ $t("message.deleteSelected") }} ({{ selectedFiles.length }})
                        </SupsisButton>
                    </PermissionController>
                    <SupsisButton v-if="selectedFiles.length > 0" icon="download" primary @click="handleBulkDownload">
                        Seçili Dosyaları indir ({{ selectedFiles.length }})
                    </SupsisButton>
                    <SupsisButton
                        icon="grid_view"
                        :ghost="viewMode === 'list'"
                        :primary="viewMode === 'grid'"
                        @click="viewMode = 'grid'"
                    />
                    <SupsisButton
                        icon="list"
                        :ghost="viewMode === 'grid'"
                        :primary="viewMode === 'list'"
                        @click="viewMode = 'list'"
                    />
                </div>
            </div>
        </div>

        <div class="toolbar-divider"></div>

        <div v-if="isLoading" class="loading-spinner">
            <a-spin size="large" />
        </div>

        <FilesCardView
            v-else-if="currentFiles.length > 0 && viewMode === 'grid'"
            :files="currentFiles"
            :selectionMode="isSelectionMode"
            :selectedFiles="selectedFiles"
            @folderDoubleClick="handleFolderDoubleClick"
            @delete="handleDelete"
            @fileSelect="handleFileSelection"
        />
        <FilesListView
            v-else-if="currentFiles.length > 0 && viewMode === 'list'"
            :files="currentFiles"
            :selectionMode="isSelectionMode"
            :selectedFiles="selectedFiles"
            @folderDoubleClick="handleFolderDoubleClick"
            @delete="handleDelete"
            @fileSelect="handleFileSelection"
        />

        <div v-else class="empty-folder-placeholder">
            <i class="material-icons">folder_open</i>
            <p>{{ $t("message.noFilesFound") }}</p>
            <PermissionController :permissions="['drive.create']">
                <SupsisButton icon="upload_file" primary @click="handleNewFile">
                    {{ $t("message.uploadFile") }}
                </SupsisButton>
            </PermissionController>
        </div>

        <!-- Upload Progress -->
        <div v-if="showUploadProgress" class="upload-progress">
            <div v-for="upload in activeUploads" :key="upload.id" class="upload-item">
                <span class="filename">{{ upload.fileName }}</span>
                <md-progress-bar md-mode="determinate" :md-value="upload.progress"></md-progress-bar>
            </div>
        </div>

        <!-- Yeni Klasör Modal -->
        <Modal2
            v-if="showNewFolderModal"
            :title="$t('message.newFolder')"
            :description="$t('message.enterFolderName')"
            :okText="$t('settings.create')"
            :cancelText="$t('message.cancel')"
            @ok="createNewFolder"
            @cancel="showNewFolderModal = false"
        >
            <div class="folder-name-input">
                <a-input
                    v-model="newFolderName"
                    :placeholder="$t('message.folderName')"
                    @keyup.enter="createNewFolder"
                    ref="folderNameInput"
                />
                <span v-if="folderNameError" class="error-text">{{ folderNameError }}</span>
            </div>
        </Modal2>

        <div v-if="isDragging" class="drop-overlay">
            <div class="drop-target">
                <i class="fas fa-cloud-upload-alt"></i>
                <p>{{ $t("message.dropFileHere") }}</p>
            </div>
        </div>

        <div v-if="showContextMenu" class="context-menu" :style="contextMenuStyle">
            <div class="context-menu-title">{{ $t("message.actions") }}</div>
            <div class="context-menu-item" @click="handleNewFile">
                <i class="fas fa-file-upload text-primary"></i> {{ $t("message.uploadFile") }}
            </div>
            <div class="context-menu-item" @click="handleNewFolder">
                <i class="fas fa-folder-plus text-success"></i> {{ $t("message.createFolder") }}
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import FilesCardView from "./FilesCardView.vue";
import FilesListView from "./FilesListView.vue";
import { DRIVE_API } from "../../../api/drive";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import { uploadAsset } from "../../../api/assetUpload";
import axios from "axios";
import log2 from "../../../utils/log2";
import { mapState } from "vuex";
import Modal2 from "../../../components/common/Modal2.vue";

const log = log2("Drive");
export default Vue.extend({
    name: "DriveHome",
    components: {
        FilesCardView,
        SupsisButton,
        Modal2,
        FilesListView,
    },
    data() {
        return {
            folders: [],
            currentFolderId: null,
            currentFiles: [],
            viewMode: "grid",
            activeUploads: [],
            showUploadProgress: false,
            showNewFolderModal: false,
            newFolderName: "",
            folderNameError: "",
            breadcrumbFolders: [],
            isDragging: false,
            dragCounter: 0,
            showContextMenu: false,
            contextMenuPosition: {
                x: 0,
                y: 0,
            },
            isLoading: false,
            selectedFiles: [],
            isSelectionMode: false,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
        contextMenuStyle() {
            return {
                top: `${this.contextMenuPosition.y}px`,
                left: `${this.contextMenuPosition.x}px`,
            };
        },
        currentFolderName() {
            if (this.currentFolderId) {
                const currentFolder = this.folders.find((f) => f._id === this.currentFolderId);
                return currentFolder ? currentFolder.name : "";
            } else {
                return "Drive";
            }
        },
    },
    async created() {
        await this.fetchFolders();
        const folderIdFromRoute = this.$route.params.id;
        this.currentFolderId = folderIdFromRoute || (this.folders.length > 0 ? this.folders[0]._id : null);

        if (this.currentFolderId) {
            await this.fetchCurrentFolderFiles();
        }
    },
    methods: {
        viewFolder(folderId) {},
        showRootFolder() {
            const root = this.folders.find((f) => f.parent === null);
            if (root) {
                this.currentFolderId = root._id;
            }
        },
        async fetchFolders() {
            try {
                this.folders = await DRIVE_API.listAllFolders();
                if (!this.currentFolderId) this.showRootFolder();
                this.updateBreadcrumb();
            } catch (error) {
                console.error("Klasörler yüklenirken hata:", error);
            }
        },
        updateBreadcrumb() {
            const currentFolder = this.folders.find((f) => f._id === this.currentFolderId);
            const breadcrumbFolders = [];

            let folder = currentFolder;
            while (folder) {
                breadcrumbFolders.unshift(folder);
                folder = this.folders.find((f) => f._id === folder.parent);
            }

            this.breadcrumbFolders = breadcrumbFolders;
        },
        navigateToFolder(folderId) {
            if (folderId === null) {
                this.showRootFolder();
            } else {
                this.currentFolderId = folderId;
            }
        },
        async fetchCurrentFolderFiles() {
            if (!this.currentFolderId) return;
            this.isLoading = true;
            try {
                this.currentFiles = await DRIVE_API.listFilesByFolderId(this.currentFolderId);
            } catch (error) {
                console.error("Dosyalar yüklenirken hata:", error);
            } finally {
                this.isLoading = false;
            }
        },
        async handleFolderDoubleClick(folderId) {
            log(`[DRIVE] Folder double clicked: ${folderId}`);
            this.currentFolderId = folderId;
        },
        handleNewFolder() {
            this.showNewFolderModal = true;
            this.newFolderName = "";
            this.folderNameError = "";
            // Input'a fokuslanmak için nextTick kullanıyoruz
            this.$nextTick(() => {
                this.$refs.folderNameInput?.focus();
            });
        },
        async handleNewFile() {
            const input = document.createElement("input");
            input.type = "file";
            input.multiple = true;

            input.onchange = async (e) => {
                const files = Array.from(e.target.files);
                this.showUploadProgress = true;

                for (const file of files) {
                    const uploadId = Math.random().toString(36);
                    this.activeUploads.push({
                        id: uploadId,
                        fileName: file.name,
                        progress: 0,
                    });

                    await this.uploadFile(file, uploadId);
                }
                this.showUploadProgress = false;
            };

            input.click();
        },
        async uploadFile(file, uploadId) {
            try {
                // Asset oluştur
                const path = `${this.site._id}/drive/${this.currentFolderId}`;
                const { data: assetData } = await uploadAsset({
                    path,
                    mime: file.type,
                    name: file.name,
                    ext: file.name.split(".").pop(),
                    size: file.size,
                    type: "drive",
                });
                log("Asset oluşturuldu", assetData);

                // S3'e yükle
                await axios({
                    method: assetData.method,
                    url: assetData.url,
                    data: file,
                    headers: { "Content-Type": file.type },
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);

                        const uploadIndex = this.activeUploads.findIndex((u) => u.id === uploadId);
                        if (uploadIndex !== -1) {
                            this.activeUploads[uploadIndex].progress = progress;
                        }
                    },
                });

                // Drive API'ye bildir
                await DRIVE_API.addAssetToFolder(this.currentFolderId, assetData.asset._id);

                // Upload listesinden kaldır
                this.activeUploads = this.activeUploads.filter((u) => u.id !== uploadId);
                if (this.activeUploads.length === 0) {
                    this.showUploadProgress = false;
                }

                // Dosya listesini güncelle
                await this.fetchCurrentFolderFiles();
            } catch (error) {
                console.error("Dosya yükleme hatası:", error);
                this.$message.error(this.$t("message.fileUploadingError"));

                const uploadIndex = this.activeUploads.findIndex((u) => u.id === uploadId);
                if (uploadIndex !== -1) {
                    this.activeUploads[uploadIndex].error = true;
                }
            }
        },
        async createNewFolder() {
            if (!this.newFolderName.trim()) {
                this.folderNameError = "Klasör adı boş olamaz";
                return;
            }

            try {
                // Yeni klasör oluştur
                await DRIVE_API.createFolder(this.newFolderName.trim(), this.currentFolderId);

                // Klasör listesini güncelle
                await this.fetchFolders();

                // Mevcut klasörün içeriğini güncelle
                await this.fetchCurrentFolderFiles();

                // Modalı kapat
                this.showNewFolderModal = false;

                // Başarı mesajı göster
                this.$message.success(this.$t("message.fileUploadingSucs"));
            } catch (error) {
                console.error("Klasör oluşturma hatası:", error);
                this.folderNameError = "Klasör oluşturulurken bir hata oluştu";
            }
        },
        handleDragEnter(event) {
            event.preventDefault();
            this.dragCounter++;
            if (this.dragCounter === 1) {
                this.isDragging = true;
            }
        },
        handleDragLeave(event) {
            event.preventDefault();
            this.dragCounter--;
            if (this.dragCounter === 0) {
                this.isDragging = false;
            }
        },
        async handleDrop(event) {
            event.preventDefault();
            this.isDragging = false;
            this.dragCounter = 0;

            const files = Array.from(event.dataTransfer.files);
            log("Dosyalar sürüklendi:", files);
            await this.uploadFiles(files);
        },
        async uploadFiles(files) {
            this.showUploadProgress = true;

            for (const file of files) {
                const uploadId = Math.random().toString(36);
                this.activeUploads.push({
                    id: uploadId,
                    fileName: file.name,
                    progress: 0,
                });

                await this.uploadFile(file, uploadId);
            }
            this.showUploadProgress = false;
        },
        handleContextMenu(event) {
            if (!event.target.closest(".file-card")) {
                event.preventDefault();
                this.showContextMenu = true;
                this.contextMenuPosition = {
                    x: event.clientX,
                    y: event.clientY,
                };
            }
        },
        hideContextMenu() {
            this.showContextMenu = false;
        },
        async handlePaste(event) {
            const items = event.clipboardData.items;
            const files = [];

            for (const item of items) {
                if (item.kind === "file") {
                    const file = item.getAsFile();
                    files.push(file);
                }
            }

            if (files.length > 0) {
                await this.uploadFiles(files);
            }
        },
        async handleDelete(file) {
            try {
                if (file.isFolder) {
                    await this.$confirm({
                        title: this.$t("message.deleteFolder"),
                        content: this.$t("message.sureDeleteFolder"),
                        okText: this.$t("message.yes"),
                        okType: "danger",
                        cancelText: this.$t("message.no"),
                        onOk: async () => {
                            await DRIVE_API.deleteFolder(file._id);
                            this.$message.success(this.$t("message.deletionSucs"));
                            await this.fetchCurrentFolderFiles();
                        },
                    });
                } else {
                    await this.$confirm({
                        title: this.$t("message.deleteFile"),
                        content: this.$t("message.sureDeleteFile"),
                        okText: this.$t("message.yes"),
                        okType: "danger",
                        cancelText: this.$t("message.no"),
                        onOk: async () => {
                            await DRIVE_API.removeMultiFiles(this.currentFolderId, [file._id]);
                            this.$message.success(this.$t("message.deletionSucs"));
                            await this.fetchCurrentFolderFiles();
                        },
                    });
                }
            } catch (error) {
                if (error.message !== "cancel") {
                    console.error("Silme hatası:", error);
                    this.$message.error(this.$t("message.deletionError"));
                }
            }
        },
        toggleSelectionMode() {
            this.isSelectionMode = !this.isSelectionMode;
            if (!this.isSelectionMode) {
                this.selectedFiles = [];
            }
        },
        handleFileSelection(file) {
            const index = this.selectedFiles.findIndex((f) => f._id === file._id);
            if (index !== -1) {
                this.selectedFiles.splice(index, 1);
                if (this.selectedFiles.length === 0) {
                    this.isSelectionMode = false;
                }
            } else {
                this.selectedFiles.push(file);
                this.isSelectionMode = true;
            }
        },
        async handleBulkDelete() {
            try {
                await this.$confirm({
                    title: this.$t("message.deleteFiles"),
                    content: this.$t("message.sureDeleteMultipleFiles", { count: this.selectedFiles.length }),
                    okText: this.$t("message.yes"),
                    okType: "danger",
                    cancelText: this.$t("message.no"),
                    onOk: async () => {
                        const fileIds = this.selectedFiles.filter((f) => !f.isFolder).map((f) => f._id);
                        const folderIds = this.selectedFiles.filter((f) => f.isFolder).map((f) => f._id);

                        if (fileIds.length > 0) {
                            await DRIVE_API.removeMultiFiles(this.currentFolderId, fileIds);
                        }

                        if (folderIds.length > 0) {
                            for (const folderId of folderIds) {
                                await DRIVE_API.deleteFolder(folderId);
                            }
                        }

                        this.$message.success(this.$t("message.deletionSucs"));
                        this.selectedFiles = [];
                        this.isSelectionMode = false;
                        await this.fetchCurrentFolderFiles();
                    },
                });
            } catch (error) {
                if (error.message !== "cancel") {
                    console.error("Toplu silme hatası:", error);
                    this.$message.error(this.$t("message.deletionError"));
                }
            }
        },
        selectAllFiles() {
            // Eğer tüm dosyalar zaten seçiliyse, seçimi kaldır
            if (this.selectedFiles.length === this.currentFiles.length) {
                this.selectedFiles = [];
                this.isSelectionMode = false;
            } else {
                // Tüm dosyaları seç
                this.selectedFiles = [...this.currentFiles];
                this.isSelectionMode = true;
            }
        },
        async handleHomeClick() {
            await this.$router.push("/drive");
            this.currentFolderId = null;
            await this.fetchFolders();
            this.showRootFolder();
            this.updateBreadcrumb();
        },
        async handleFolderClick(folderId) {
            await this.$router.push({ name: "Drive Detail", params: { id: folderId } });
            this.currentFolderId = folderId;
            await this.fetchCurrentFolderFiles();
            this.updateBreadcrumb();
        },
        async handleBulkDownload() {
            if (this.selectedFiles.length === 0) return;

            const filesToDownload = this.selectedFiles.filter(file => !file.isFolder && file.asset?.cdnUrl);

            if (filesToDownload.length === 0) {
                this.$message.warning(this.$t("message.noDownloadableFiles"));
                return;
            }

            if (filesToDownload.length > 1) {
                this.$message.info(`${filesToDownload.length} dosya ayrı ayrı indirilecek...`);
            }

            for (let i = 0; i < filesToDownload.length; i++) {
                const file = filesToDownload[i];

                if (i > 0) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }

                try {
                    const response = await fetch(file.asset.cdnUrl, {
                        method: "GET",
                        mode: "cors",
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);

                    const link = document.createElement("a");
                    link.href = blobUrl;
                    link.download = file.name;
                    link.style.display = "none";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    URL.revokeObjectURL(blobUrl);
                } catch (error) {
                    console.error(`Fetch download failed for ${file.name}, trying alternative methods:`, error);

                    try {
                        const link = document.createElement("a");
                        link.href = file.asset.cdnUrl;
                        link.download = file.name;
                        link.target = "_blank";
                        link.rel = "noopener noreferrer";
                        link.style.display = "none";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        console.log(`${file.name} yeni sekmede açıldı`);
                    } catch (fallbackError) {
                        console.error(`All download methods failed for ${file.name}:`, fallbackError);
                        this.$message.error(`${file.name} indirilemedi`);
                    }
                }
            }

            this.$message.success(`${filesToDownload.length} dosya indirilmeye başlandı`);
        },
    },
    watch: {
        currentFolderId() {
            this.fetchCurrentFolderFiles();
            this.updateBreadcrumb();
        },
        "$route.params.id": {
            immediate: true,
            handler(newId) {
                if (newId) {
                    this.currentFolderId = newId;
                    this.fetchCurrentFolderFiles();
                } else {
                    this.currentFolderId = null;
                    this.fetchFolders();
                    this.showRootFolder();
                }
                this.updateBreadcrumb();
            },
        },
    },
    mounted() {
        document.addEventListener("click", this.hideContextMenu);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.hideContextMenu);
    },
});
</script>

<style scoped lang="scss">
.drive-home {
    height: 100%;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    min-height: 100vh;
    padding: 20px;
}

.drive-header {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    padding: 24px 32px;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.2);
    position: relative;
    overflow: hidden;
    margin-bottom: 24px;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    }

    .ant-breadcrumb {
        font-size: 15px;
        font-weight: 500;
        display: flex;
        align-items: center;
        padding: 8px 0;
        overflow-x: auto;
        white-space: nowrap;

        &-separator {
            margin: 0 12px;
            color: #94a3b8;
            font-size: 14px;
        }

        &-link,
        &-item {
            display: inline-flex;
            align-items: center;
            padding: 8px 16px;
            border-radius: 12px;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            font-weight: 500;

            &:not(:last-child) {
                margin-right: 8px;
            }
        }

        &-link {
            color: #667eea;
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.2);

            &:hover {
                color: #ffffff;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                text-decoration: none;
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
            }
        }

        &-item:last-child {
            color: #1e293b;
            font-weight: 600;
            background: rgba(30, 41, 59, 0.08);
            border: 1px solid rgba(30, 41, 59, 0.1);
        }

        .anticon {
            font-size: 18px;
            margin-right: 10px;
            transition: color 0.3s ease;
        }
    }
}

.drive-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px 24px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.3);

    .left-actions {
        display: flex;
        gap: 12px;
        align-items: center;
    }

    .right-actions {
        display: flex;
        align-items: center;
        gap: 12px;

        .view-toggle {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 4px;
            background: rgba(0, 0, 0, 0.05);
            border-radius: 12px;
        }
    }
}

.toolbar-divider {
    display: none;
}

.empty-folder-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 40px;
    margin: 40px;
    border: 2px dashed rgba(102, 126, 234, 0.3);
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);

    i.material-icons {
        font-size: 64px;
        color: #667eea;
        margin-bottom: 24px;
        opacity: 0.8;
    }

    p {
        color: #64748b;
        font-size: 18px;
        margin-bottom: 24px;
        font-weight: 500;
    }
}

.upload-progress {
    position: fixed;
    bottom: 24px;
    right: 24px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    padding: 20px;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    min-width: 320px;
    z-index: 1000;
    border: 1px solid rgba(255, 255, 255, 0.3);

    .upload-item {
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 0;
        }

        .filename {
            display: block;
            margin-bottom: 8px;
            font-size: 14px;
            color: #475569;
            font-weight: 500;
        }
    }
}

.folder-name-input {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .error-text {
        color: #ef4444;
        font-size: 12px;
        font-weight: 500;
    }
}

.drop-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.drop-target {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    padding: 48px;
    border-radius: 24px;
    text-align: center;
    box-shadow: 0 16px 64px rgba(0, 0, 0, 0.2);
    border: 2px dashed #667eea;

    i {
        font-size: 4rem;
        color: #667eea;
        margin-bottom: 16px;
    }

    p {
        font-size: 1.25rem;
        color: #1e293b;
        font-weight: 500;
    }
}

.context-menu {
    position: fixed;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    padding: 8px 0;
    z-index: 1000;
    border: 1px solid rgba(255, 255, 255, 0.3);
    min-width: 180px;

    .context-menu-title {
        font-weight: 600;
        padding: 12px 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        color: #1e293b;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .context-menu-item {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        cursor: pointer;
        transition: all 0.2s ease;
        font-weight: 500;
        color: #475569;

        &:hover {
            background: rgba(102, 126, 234, 0.1);
            color: #667eea;
        }

        i {
            margin-right: 12px;
            font-size: 16px;
        }

        .text-primary {
            color: #667eea;
        }

        .text-success {
            color: #10b981;
        }
    }
}

.loading-spinner {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
    
    .ant-spin {
        color: #667eea;
    }
}
</style>
